/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  Button,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';
import {
  PrenomsInput,
  OPTIONS_SEXE,
  _string,
} from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import {
  buildEnfantUpdatePayload,
  buildIndividuPutPayload,
  parseIndividuFiche,
} from '@/utils';

interface ModalEnfantProps {
  /**
   * Endpoint générique des individus d'acte. Quand il est fourni,
   * l'enregistrement passe par `PUT {urlIndividus}/{id}` au lieu du PUT du
   * dossier de numérisation (qui n'existe pas pour tous les types d'acte).
   */
  urlIndividus?: string;
  open: boolean;
  /** Identifiant de la fiche (celui de l'acte), utilisé pour l'URL de `service.update`. */
  identifiant?: number;
  enfant?: any;
  /** Instance `service` de `useFiche` fournie par `ActeFicheContainer` (celle de la lecture). */
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (enfant: any) => void;
}

/**
 * Périmètre volontairement limité à l'état civil : la date, l'heure et le lieu
 * de naissance de l'enfant sont édités depuis le panel « EVENEMENT ».
 */
const CHAMPS_EDITABLES = [
  'nomFamille',
  // Plus saisissable (aligné sur `ModalParent`, qui ne l'expose pas non plus),
  // mais conservé ici pour que la valeur lue soit réémise à l'identique : sans
  // ça, modifier l'enfant effacerait un nom d'usage déjà en base.
  'nomUsage',
  'prenom',
  'prenomsComplementaires',
  'sexe',
];

const ModalEnfant = (props: ModalEnfantProps) => {
  const { open, identifiant, enfant, urlIndividus, service, onClose, onSaved } =
    props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData(_.pick(parseIndividuFiche(enfant), CHAMPS_EDITABLES));
    setAttempted(false);
  }, [open, enfant]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleNomChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: string },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({
        ...prev,
        [name]: _string.normalizeNomFamille(value),
      }));
    },
    [],
  );

  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};
    if (_.isEmpty(data?.nomFamille)) {
      e.nomFamille = 'Le nom de famille est obligatoire';
    }
    if (_.isEmpty(data?.prenom)) {
      e.prenom = 'Le prénom usuel est obligatoire';
    }
    if (_.isEmpty(data?.sexe)) {
      e.sexe = 'La civilité est obligatoire';
    }
    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    // Deux chemins d'écriture, selon ce que le type d'acte expose :
    // - `urlIndividus` fourni : endpoint générique de l'individu. C'est le cas
    //   des DEUX types d'acte aujourd'hui ; le référentiel des professions y
    //   reste alimenté (`ActeIndividuRepository::update`) ;
    // - sinon : repli sur le PUT du dossier, pour un type d'acte qui n'aurait
    //   pas encore `urlIndividus` au registre.
    const payload = buildEnfantUpdatePayload(
      identifiant as number,
      enfant,
      data,
    );
    const enregistrement = urlIndividus
      ? request.put(
          `${urlIndividus}/${enfant?.identifiant}`,
          buildIndividuPutPayload(enfant, data),
        )
      : service?.update(payload);

    // Ni endpoint d'individu, ni service : rien à quoi envoyer la modification.
    if (!enregistrement) {
      return;
    }

    setSaving(true);
    enregistrement
      .then(() => {
        onSaved({ ...enfant, ...payload.enfant });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'enfant",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    errors,
    service,
    identifiant,
    enfant,
    urlIndividus,
    data,
    onSaved,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier l'enfant" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Grid>
            <Grid.Row>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Tooltip
                  trigger={
                    <Form.Dropdown
                      selection
                      required
                      name="sexe"
                      label="Sexe"
                      placeholder="Sexe"
                      options={OPTIONS_SEXE}
                      value={data?.sexe ?? ''}
                      error={!_.isNil(displayedErrors.sexe)}
                      onChange={handleChange}
                    />
                  }
                  type={displayedErrors.sexe ? 'error' : ''}
                  message={displayedErrors.sexe}
                />
              </Grid.Column>
              <Grid.Column computer={12} tablet={8} mobile={16}>
                <Tooltip
                  trigger={
                    <Form.Input
                      required
                      name="nomFamille"
                      label="Nom"
                      value={data?.nomFamille ?? ''}
                      error={!_.isNil(displayedErrors.nomFamille)}
                      onChange={handleNomChange}
                    />
                  }
                  type={displayedErrors.nomFamille ? 'error' : ''}
                  message={displayedErrors.nomFamille}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <PrenomsInput
                  prenom={data?.prenom ?? ''}
                  prenomsComplementaires={data?.prenomsComplementaires ?? ''}
                  errors={displayedErrors}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEnfant;
